"use client";
import React from "react";
import Link from "next/link";
import styles from "./blog.module.css";
import { IconCalendar, IconPhoto } from "@/lib/icons";
import dayjs from "dayjs";
import SafeHtml from "../CMS/SafeHtml";
import AppImage from "../common/AppImage";

// Cards (blog section, listing grid, slider) show the dedicated
// `listing_image`; the cover `image` is the detail page's hero now (see
// BlogDetail.js) and only fills in when a blog has no listing image.
function resolveBlogImage(item, detailData) {
  const candidates = [
    item?.listing_image,
    item?.content?.listing_image,
    item?.meta?.listing_image,
    detailData?.listing_image,
    item?.image,
    item?.feature_image,
    item?.cover_image,
    item?.thumbnail,
    item?.bg_image,
    item?.meta?.feature_image,
    item?.meta?.image,
    item?.content?.feature_image,
    detailData?.image,
    detailData?.feature_image,
    detailData?.cover_image,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string" && candidate.trim())
      return candidate.trim();
    if (Array.isArray(candidate) && candidate.length > 0) {
      const first = candidate[0];
      if (typeof first === "string" && first.trim()) return first.trim();
      if (typeof first === "object" && first !== null) {
        const url =
          first.md || first.sd || first.thumbnail || first.url || first.src;
        if (url) return url;
      }
    }
    if (typeof candidate === "object" && candidate !== null) {
      const url =
        candidate.md ||
        candidate.sd ||
        candidate.thumbnail ||
        candidate.url ||
        candidate.src;
      if (url) return url;
    }
  }

  if (Array.isArray(item?.content?.sections)) {
    for (const sec of item.content.sections) {
      const secData = sec?.data || sec?.config;
      if (secData?.image) {
        const res = resolveBlogImage({ image: secData.image });
        if (res) return res;
      }
    }
  }

  return "";
}

export default function BlogCard({ item, redirectUrl, style }) {
  if (!item) return null;

  const blogDetailSection = item.content?.sections?.find(
    (s) => s.type === "BlogDetail",
  );
  const detailData = blogDetailSection?.data || {};

  const imgSrc = resolveBlogImage(item, detailData);
  const title = item.title || item.name || detailData.title || "Untitled";

  const rawExcerpt =
    item.excerpt ||
    item.description ||
    item.meta?.description ||
    detailData.description ||
    "";
  const excerpt =
    typeof rawExcerpt === "string"
      ? rawExcerpt
          .replace(/<[^>]+>/g, "")
          .trim()
          .slice(0, 120) + (rawExcerpt.length > 120 ? "..." : "")
      : "";

  const slug = item.slug;

  const rawDate =
    item?.date ||
    item?.published_at ||
    item?.created_at ||
    item?.content?.sections?.[0]?.data?.date ||
    detailData?.date;

  const formattedDate = rawDate
    ? dayjs(rawDate).isValid()
      ? dayjs(rawDate).format("MMMM D, YYYY")
      : rawDate.split("T")[0]
    : null;

  // Extract author info
  const resolveAuthorName = (data) => {
    if (!data) return "Tykkit Team";
    const raw =
      data.authorName ||
      data.author?.name ||
      (typeof data.author === "string" ? data.author : null) ||
      data.written_by?.name ||
      (typeof data.written_by === "string" ? data.written_by : null) ||
      data.created_by?.name ||
      (typeof data.created_by === "string" ? data.created_by : null);
    if (typeof raw === "string") return raw;
    if (typeof raw === "object" && raw !== null) {
      return raw.name || raw.title || raw.username || "Tykkit Team";
    }
    return String(raw || "Tykkit Team");
  };

  const authorName1 = resolveAuthorName(item);
  const authorName2 = resolveAuthorName(detailData);
  const authorName = authorName1 !== "Tykkit Team" ? authorName1 : (authorName2 !== "Tykkit Team" ? authorName2 : "Tykkit Team");
  
  const rawAvatar = item.author?.avatar || item.author_avatar || detailData.author?.avatar || detailData.author_avatar;
  const avatarUrl = typeof rawAvatar === "string" ? rawAvatar : (rawAvatar?.url || rawAvatar?.src || "");
  const authorInitial = typeof authorName === "string" && authorName.trim() ? authorName.trim().charAt(0).toUpperCase() : "T";

  const href = redirectUrl
    ? redirectUrl(item)
    : item.url?.url
      ? item.url.url.startsWith("/")
        ? item.url.url
        : `/${item.url.url}`
      : slug
        ? slug.startsWith("/")
          ? slug
          : slug.startsWith("blogs/")
            ? `/${slug}`
            : `/blogs/${slug}`
        : "#";

  return (
    <div className={styles.blogCard} style={style}>
      <Link href={href} className={styles.blogCardLink}>
        {/* Image */}
        <div className={styles.blogCardImage}>
          {imgSrc ? (
            <AppImage
              src={imgSrc}
              alt={title}
              fill
              className="object-cover w-full h-full"
            />
          ) : (
            <div className={styles.blogCardPlaceholder}>
              <IconPhoto size={36} stroke={1} color="#bbb" />
            </div>
          )}
        </div>

        {/* Body */}
        <div className={styles.blogCardBody}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            {avatarUrl && (
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#021526', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, overflow: 'hidden', flexShrink: 0 }}>
                <img src={avatarUrl} alt={authorName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
              {authorName} &bull; {formattedDate}
            </span>
          </div>
          <h3 className={styles.blogCardTitle}>{title}</h3>
          {excerpt && (
            <div className={styles.blogCardExcerpt}>
              <SafeHtml html={excerpt} />
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}
